import React, { useState, useEffect } from "react";
import { 
  Calendar, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertCircle, 
  FileText, 
  Plus, 
  Search,
  CalendarDays,
  ArrowRight,
  User,
  SlidersHorizontal
} from "lucide-react";
import Header from "../../components/Header";
import LeaveFormModal from "../../employee/leaves/LeaveFormModal";
import api from "../../connector/api";

export default function MyLeave() {
  const [myLeaves, setMyLeaves] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [searchQuery, setSearchQuery] = useState("");
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const userRes = await api.get("/accounts/me/");
      if (userRes.data && userRes.data.user) {
        setUser(userRes.data.user);
      } else {
        setUser(userRes.data);
      }
      
      const myRes = await api.get("/leaves/my-requests/");
      let dataPayload = Array.isArray(myRes.data) ? myRes.data : myRes.data.results || [];
      setMyLeaves(dataPayload);
    } catch (err) {
      console.error("Dashboard engine failed to stream personal layout metrics:", err);
    } finally {
      setLoading(false);
    }
  };

  // Place this inside your MyLeave component, replacing the old useEffect block
  useEffect(() => {
    // 1. Initial page load fetch
    fetchDashboardData();

    // 2. 🔌 Real-time handler linking to your Header's WebSocket broadcast
    const handleLiveRefresh = () => {
      console.log("🔄 Real-time event captured! Re-syncing dashboard states...");
      fetchDashboardData();
    };

    // Add the listener to the window object
    window.addEventListener("refreshLeaveDashboard", handleLiveRefresh);

    // Clean up listener when user navigates away from this page
    return () => {
      window.removeEventListener("refreshLeaveDashboard", handleLiveRefresh);
    };
  }, []);

  const handleLeaveSubmitted = () => {
    fetchDashboardData();
  };

  const totalRequested = myLeaves.length;
  const approvedCount = myLeaves.filter(l => l.status === "APPROVED").length;
  const pendingCount = myLeaves.filter(l => l.status === "PENDING").length;

  const filteredLeaves = myLeaves
    .filter(l => filterStatus === "ALL" || l.status === filterStatus)
    .filter(l => (l.leave_type || "").toLowerCase().includes(searchQuery.toLowerCase()));

  const statusStyles = {
    APPROVED: { 
      bg: "bg-emerald-500/10 border-emerald-500/20 text-emerald-600 dark:text-emerald-400", 
      icon: <CheckCircle size={14} /> 
    },
    REJECTED: { 
      bg: "bg-rose-500/10 border-rose-500/20 text-rose-600 dark:text-rose-400", 
      icon: <XCircle size={14} /> 
    },
    PENDING: { 
      bg: "bg-amber-500/10 border-amber-500/20 text-amber-600 dark:text-amber-400", 
      icon: <Clock size={14} /> 
    }
  };

  const openEditModal = (leave) => {
    setSelectedLeave(leave);
    setIsSubmitModalOpen(true);
  };

  const handleModalClose = () => {
    setSelectedLeave(null);
    setIsSubmitModalOpen(false);
  };

  return (
    <div className="min-h-screen bg-[#fafafa] text-slate-900 antialiased font-dynamic-title selection:bg-indigo-500/10 selection:text-indigo-600">
      <Header />

      <main className="max-w-6xl mx-auto px-4 py-10 space-y-8">
        
        {/* 🏷️ Header Hero Panel */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-100 pb-4">
          <div>
            <span className="text-[8px] font-bold text-[#002855] uppercase tracking-wider bg-slate-100 px-2 py-0.5 rounded-md border border-slate-200/40">
              Personal Workspace
            </span>
            <h1 className="text-lg font-extrabold text-slate-900 tracking-tight mt-1.5">Time-off Overview</h1>
            <p className="text-xs text-slate-500 mt-0.5">Welcome back, <span className="font-medium text-slate-800">{user?.first_name || "Employee"}</span>. Review balance allocations.</p>
          </div>

          <button
            onClick={() => {
              setSelectedLeave(null);
              setIsSubmitModalOpen(true);
            }}
            className="inline-flex items-center justify-center gap-2 h-9 px-4 bg-[#002855] bg-opacity-80 hover:bg-opacity-95 text-white rounded-xl text-xs font-medium transition shadow-sm active:scale-[0.98]"
          >
            <Plus size={16} />
            <span>Request Time-off</span>
          </button>
        </div>

        {/* 📊 Metrics Matrix Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="relative overflow-hidden bg-white p-3 rounded-xl border border-slate-200/60 flex items-center gap-4">
            <div className="p-3 bg-slate-50 text-slate-600 rounded-lg border border-slate-100">
              <CalendarDays size={15} />
            </div>
            <div>
              <span className="block text-xs font-medium text-slate-400">Total Submissions</span>
              <span className="text-sm font-semibold text-slate-800 mt-0.5 block">{totalRequested}</span>
            </div>
          </div>

          <div className="relative overflow-hidden bg-white p-3 rounded-xl border border-slate-200/60 flex items-center gap-4">
            <div className="p-3 bg-amber-50 text-amber-600 rounded-lg border border-amber-100/60">
              <Clock size={15} />
            </div>
            <div>
              <span className="block text-xs font-medium text-slate-400">Awaiting Approval</span>
              <span className="text-sm font-semibold text-slate-800 mt-0.5 block">{pendingCount}</span>
            </div>
          </div>

          <div className="relative overflow-hidden bg-white p-3 rounded-xl border border-slate-200/60 flex items-center gap-4">
            <div className="p-3 bg-emerald-50 text-emerald-600 rounded-lg border border-emerald-100/60">
              <CheckCircle size={15} />
            </div>
            <div>
              <span className="block text-xs font-medium text-slate-400">Approved Balance</span>
              <span className="text-sm font-semibold text-slate-800 mt-0.5 block">{approvedCount}</span>
            </div>
          </div>
        </div>

        {/* 🔍 Filters Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl w-fit overflow-x-auto">
            {["ALL", "PENDING", "APPROVED", "REJECTED"].map((status) => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`h-7 px-3 rounded-md text-xs font-medium transition-all duration-150 whitespace-nowrap ${
                  filterStatus === status
                    ? "bg-white text-slate-900 shadow-xs"
                    : "text-slate-500 hover:text-slate-900"
                }`}
              >
                {status === "ALL" ? "All History" : status.charAt(0) + status.slice(1).toLowerCase()}
              </button>
            ))}
          </div>

          <div className="relative min-w-[260px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Filter by context / category..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-9 bg-white border border-slate-200 focus:border-[#002855] border-opacity-80 rounded-xl pl-9 pr-4 text-xs outline-none transition-all"
            />
          </div>
        </div>

        {/* 📄 Leave Requests Stack Feed */}
        <div className="space-y-3">
          {filteredLeaves.length === 0 ? (
            <div className="bg-white border border-dashed border-slate-200 rounded-xl p-12 text-center text-slate-400">
              <AlertCircle size={24} className="mx-auto text-slate-300 mb-2" />
              <p className="text-xs font-medium">No records matching criteria found.</p>
            </div>
          ) : (
            filteredLeaves.map((leave) => {
              const layoutConfig = statusStyles[leave.status] || statusStyles.PENDING;

              return (
                <div 
                  key={leave.id}
                  onClick={() => openEditModal(leave)}
                  className="group bg-white border border-slate-200/70 hover:border-slate-300 rounded-xl p-4 transition-all duration-200 cursor-pointer flex flex-col sm:flex-row justify-between sm:items-center gap-4"
                >
                  <div className="flex items-start gap-3.5 min-w-0">
                    <div className="p-2.5 rounded-lg bg-slate-50 border border-slate-100 text-slate-400 group-hover:text-[#002855] group-hover:bg-indigo-50/30 transition-colors shrink-0">
                      <Calendar size={14} />
                    </div>

                    <div className="space-y-1.5 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-xs text-slate-900 truncate uppercase">
                          {leave.leave_type}
                        </h3>
                      </div>

                      {leave.reason ? (
                        <p className="text-xs text-slate-500 truncate max-w-xl flex items-center gap-1.5">
                          <FileText size={13} className="text-slate-300 shrink-0" />
                          <span>{leave.reason}</span>
                        </p>
                      ) : (
                        <p className="text-xs text-slate-400 italic">No notes provided.</p>
                      )}

                      <div className="text-xs text-slate-500 flex items-center gap-1.5 pt-0.5">
                        <span className="font-medium text-slate-700 bg-slate-50 border border-slate-100 px-1.5 py-0.5 rounded text-[11px]">
                          {leave.start_date}
                        </span>
                        <ArrowRight size={12} className="text-slate-300" />
                        <span className="font-medium text-slate-700 bg-slate-50 border border-slate-100 px-1.5 py-0.5 rounded text-[11px]">
                          {leave.end_date}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between sm:justify-end gap-3 self-stretch sm:self-auto pt-3 sm:pt-0 border-t sm:border-t-0 border-slate-50">
                    <div className={`h-6 px-2.5 rounded-md font-semibold text-[11px] tracking-wide flex items-center gap-1.5 border ${layoutConfig.bg}`}>
                      {layoutConfig.icon}
                      <span className="capitalize">{leave.status.toLowerCase()}</span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </main>

      <LeaveFormModal
        isOpen={isSubmitModalOpen}
        onClose={handleModalClose}
        onLeaveSubmitted={handleLeaveSubmitted}
        selectedLeave={selectedLeave}
      />
    </div>
  );
}